import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import useNotification from "../../hooks/useNotification";
import UserManagementService from "../../services/userManagementService";

import { useAuth } from "../../contexts/AuthContext";

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FormErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
  open,
  onClose,
}) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const { showNotification } = useNotification();
  const { user } = useAuth();

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!currentPassword)
      newErrors.currentPassword = "Current password is required";
    if (!newPassword) newErrors.newPassword = "New password is required";
    if (newPassword.length < 8)
      newErrors.newPassword = "Password must be at least 8 characters long";
    if (!confirmPassword)
      newErrors.confirmPassword = "Please confirm your new password";
    if (newPassword !== confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    switch (field) {
      case "currentPassword":
        setCurrentPassword(value);
        break;
      case "newPassword":
        setNewPassword(value);
        break;
      case "confirmPassword":
        setConfirmPassword(value);
        break;
    }
    // Clear error for the changed field
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!user?.adminID) {
      showNotification(
        "User ID is not available. Please try logging in again.",
        "error"
      );
      return;
    }
    try {
      await UserManagementService.changePassword(
        user?.adminID,
        currentPassword,
        newPassword
      );
      showNotification("Password changed successfully", "success");
      onClose();
    } catch (error) {
      console.error("Error changing password:", error);
      showNotification("Failed to change password. Please try again.", "error");
    }
  };
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "400px",
          maxWidth: "90%",
        },
      }}
    >
      <DialogTitle>
        <FontAwesomeIcon icon={faLock} /> Change Password
      </DialogTitle>
      <DialogContent
        sx={{
          height: "280px", // Set a fixed height
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) =>
              handleInputChange("currentPassword", e.target.value)
            }
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            fullWidth
          />
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => handleInputChange("newPassword", e.target.value)}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            fullWidth
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Change Password
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
